/**
 * TranslatedText — wraps participant-authored report text with an
 * [AI translation] badge and optional original-on-expand when the viewer
 * is reading a translated report and this field has a stored translation.
 */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useReportEdits } from './ReportEditsContext';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import EditableText from './EditableText';

const Wrap = styled.div`
  display: ${p => (p.$block ? 'block' : 'inline')};
`;

const Badge = styled.button`
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  font-size: 0.68rem;
  font-weight: 600;
  color: #6d28d9;
  background: rgba(124, 58, 237, 0.08);
  border: 1px solid rgba(124, 58, 237, 0.2);
  border-radius: 4px;
  padding: 1px 6px;
  cursor: pointer;
  vertical-align: middle;
  &:hover { background: rgba(124, 58, 237, 0.14); }
  @media print { border: none; background: transparent; }
`;

const Original = styled.div`
  margin-top: 6px;
  padding: 8px 10px;
  font-size: 0.9em;
  color: #6b7280;
  background: #f9fafb;
  border-left: 2px solid #e5e7eb;
  border-radius: 0 6px 6px 0;
`;

export default function TranslatedText({
  fieldKey,
  fallback,
  label,
  markdown = false,
  block = false,
  multiline = true,
  ...rest
}) {
  const { isTranslated, get, getOriginal, isTranslatedView } = useReportEdits();
  const { t } = useTranslation();
  const [showOriginal, setShowOriginal] = useState(false);
  const original = getOriginal(fieldKey, fallback);
  const displayed = get(fieldKey, fallback);
  // Hide badge when stored "translation" is a no-op (same as original)
  const translated = isTranslated(fieldKey)
    && String(displayed).trim() !== String(original).trim();

  return (
    <Wrap $block={block}>
      <EditableText
        fieldKey={fieldKey}
        fallback={fallback}
        label={label}
        markdown={markdown}
        block={block}
        multiline={multiline}
        {...rest}
      />
      {isTranslatedView && translated && (
        <>
          <Badge
            type="button"
            onClick={() => setShowOriginal((v) => !v)}
            title={showOriginal ? t('report.translation.hideOriginal') : t('report.translation.showOriginal')}
          >
            {t('report.translation.badge')}
          </Badge>
          {showOriginal && (
            <Original>
              {getOriginal(fieldKey, fallback)}
            </Original>
          )}
        </>
      )}
    </Wrap>
  );
}
